import { clamp } from "@redotech/util/clamp";
import type Quill from "quill";

export const MIN_FONT_SIZE = 6;
export const MAX_FONT_SIZE = 72;

/** The slice of Quill these helpers touch; narrow so it can be faked in specs. */
export type FontSizeEditor = Pick<
  Quill,
  "getContents" | "getFormat" | "setSelection"
> & {
  selection: Pick<Quill["selection"], "savedRange" | "format">;
  editor: Pick<Quill["editor"], "formatText">;
};

interface TextRun {
  index: number;
  length: number;
  size: number;
}

export function parseFontSizePx(rawSize: unknown, defaultSize: number): number {
  if (typeof rawSize !== "string") {
    return defaultSize;
  }
  const parsed = Number(rawSize.replace(/px$/, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : defaultSize;
}

export function clampFontSize(size: number): number {
  return clamp(Math.round(size), MIN_FONT_SIZE, MAX_FONT_SIZE);
}

/** Text runs inside a range, each with its effective size; newlines and embeds are skipped. */
function textRuns(
  quill: FontSizeEditor,
  index: number,
  length: number,
  defaultSize: number,
): TextRun[] {
  const runs: TextRun[] = [];
  let offset = index;
  for (const op of quill.getContents(index, length).ops) {
    if (typeof op.insert !== "string") {
      offset += 1;
      continue;
    }
    const size = parseFontSizePx(op.attributes?.size, defaultSize);
    let start = 0;
    for (const part of op.insert.split(/(\n)/)) {
      if (part.length > 0 && part !== "\n") {
        runs.push({ index: offset + start, length: part.length, size });
      }
      start += part.length;
    }
    offset += op.insert.length;
  }
  return runs;
}

/**
 * Size at the last known range: a collapsed cursor reports its pending size,
 * a selection reports its single size, and `null` means mixed sizes.
 */
export function readFontSize(
  quill: FontSizeEditor,
  defaultSize: number,
): number | null {
  const range = quill.selection.savedRange;
  if (range == null) {
    return defaultSize;
  }
  try {
    if (range.length === 0) {
      return parseFontSizePx(quill.getFormat(range).size, defaultSize);
    }
    const sizes = new Set(
      textRuns(quill, range.index, range.length, defaultSize).map(
        (run) => run.size,
      ),
    );
    if (sizes.size === 0) {
      return defaultSize;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return sizes.size === 1 ? [...sizes][0]! : null;
  } catch {
    // getFormat/getContents can throw on malformed document content
    return defaultSize;
  }
}

/**
 * Formats a selected range in place (keeping it selected), or sets a pending
 * size for the next typed text at a collapsed cursor. Returns whether the document changed.
 */
export function applyFontSize(quill: FontSizeEditor, size: number): boolean {
  const range = quill.selection.savedRange;
  if (range == null) {
    return false;
  }
  const value = `${clampFontSize(size)}px`;
  if (range.length === 0) {
    quill.selection.format("size", value);
    return false;
  }
  quill.editor.formatText(range.index, range.length, { size: value });
  quill.setSelection(range.index, range.length, "silent");
  return true;
}

/**
 * Steps every text run in the selection by `delta` relative to its own size,
 * so mixed-size selections keep their differences (as in Docs and Word).
 */
export function stepFontSize(
  quill: FontSizeEditor,
  delta: number,
  defaultSize: number,
): boolean {
  const range = quill.selection.savedRange;
  if (range == null) {
    return false;
  }
  if (range.length === 0) {
    const current = parseFontSizePx(quill.getFormat(range).size, defaultSize);
    quill.selection.format("size", `${clampFontSize(current + delta)}px`);
    return false;
  }
  const runs = textRuns(quill, range.index, range.length, defaultSize);
  for (const run of runs) {
    quill.editor.formatText(run.index, run.length, {
      size: `${clampFontSize(run.size + delta)}px`,
    });
  }
  quill.setSelection(range.index, range.length, "silent");
  return runs.length > 0;
}
